import { type ProjectCode } from '@constants'
import { Pagination } from '@fortune-ui'
import { useApiECommerceOrders } from '@hooks-api/ec/useApiECommerceOrders'
import { type WpOrderStatus, type WpOrderType, WP_ORDER_STATUS, WP_ORDER_TYPE } from '@hooks-api/types/dataModel/eCommerceOrders'
import { Box } from '@pets-ui/Box/Box'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { SnackbarProvider, SnackbarContext } from '@pets-ui/Snackbar/SnackbarProvider'
import { SnackbarVariants } from '@pets-ui/Snackbar/SnackbarVariants'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import '@wp-types/global.d.ts'
import queryString from 'query-string'
import { useState, useEffect, useRef, type ChangeEvent, type SyntheticEvent } from 'react'
import { Helmet } from 'react-helmet-async'
import { I18nextProvider } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom-v5'
import { OrderEmptyCard } from './components/OrderEmptyCard.tsx'
import { OrderList } from './components/OrderList'
import { Tabs } from './components/Tabs'
import wpMyAccountI18nInstance from './i18n/i18nConfig.ts'
import { WpMyAccountContext, type ContextWpMyAccountProps } from './useContextWpMyAccount.ts'

interface WpOrderProps {
  type: WpOrderType
  environment: ContextWpMyAccountProps['environment']
  siteId?: number
  projectCode: ProjectCode
  onRepayClick?: () => void
  onCancelOrderClick?: () => void
  campaignId?: number
}

const ORDER_TABS_DATA = [
  { label: wpMyAccountI18nInstance.t('orderTab.all'), value: WP_ORDER_STATUS.ALL },
  { label: wpMyAccountI18nInstance.t('orderTab.pending'), value: WP_ORDER_STATUS.PENDING },
  { label: wpMyAccountI18nInstance.t('orderTab.processing'), value: WP_ORDER_STATUS.PROCESSING },
  { label: wpMyAccountI18nInstance.t('orderTab.completed'), value: WP_ORDER_STATUS.COMPLETED },
  { label: wpMyAccountI18nInstance.t('orderTab.cancelled'), value: WP_ORDER_STATUS.CANCELLED },
]

const SUBSCRIBE_ORDER_TABS_DATA = [
  { label: wpMyAccountI18nInstance.t('orderTabWarehouse.all'), value: WP_ORDER_STATUS.ALL },
  { label: wpMyAccountI18nInstance.t('orderTabWarehouse.pending'), value: WP_ORDER_STATUS.PENDING },
  { label: wpMyAccountI18nInstance.t('orderTabWarehouse.processing'), value: WP_ORDER_STATUS.PROCESSING },
  { label: wpMyAccountI18nInstance.t('orderTabWarehouse.completed'), value: WP_ORDER_STATUS.COMPLETED },
  { label: wpMyAccountI18nInstance.t('orderTabWarehouse.cancelled'), value: WP_ORDER_STATUS.CANCELLED },
]

const TABS_WORDING_MAP = {
  [WP_ORDER_TYPE.NORMAL]: ORDER_TABS_DATA,
  [WP_ORDER_TYPE.PAYMENT_TRANSACTION]: ORDER_TABS_DATA,
  [WP_ORDER_TYPE.SUBSCRIBE]: SUBSCRIBE_ORDER_TABS_DATA,
  [WP_ORDER_TYPE.SERVICE_ORDER]: SUBSCRIBE_ORDER_TABS_DATA,
  [WP_ORDER_TYPE.CAMPAIGN]: ORDER_TABS_DATA,
}

export function WpOrder({ type, environment, siteId: siteIdProp, projectCode, onRepayClick, onCancelOrderClick, campaignId }: WpOrderProps) {
  const isWordPressMobile = useResponsive('wordpressMobile')
  const history = useHistory()
  const { search } = useLocation()
  const defaultTabValue = queryString.parse(search).tabValue as WpOrderStatus || WP_ORDER_STATUS.ALL
  const [tabValue, setTabValue] = useState<WpOrderStatus>(defaultTabValue)
  const [page, setPage] = useState(1)
  const [focusedTransactionId, setFocusedTransactionId] = useState<string | null>(null)
  const hasFocusedRef = useRef(false)
  const handleChangeTab = (_event: SyntheticEvent, newValue: WpOrderStatus) => {
    setPage(1)
    setFocusedTransactionId(null)
    hasFocusedRef.current = false
    history.replace({ search: `?tabValue=${newValue}` })
  }

  const handleChangePage = (_event: ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const siteId = window?.general_config?.multisite?.site_id || siteIdProp
  const { destructuredData: eCommerceOrders, isLoading } = useApiECommerceOrders(
    siteId
      ? {
        project_code: projectCode,
        site_id: siteId,
        status: tabValue,
        type,
        page: page - 1,
        campaign_id: campaignId,
      }
      : undefined)

  useEffect(() => {
    const tabFromUrl = queryString.parse(search).tabValue as WpOrderStatus || WP_ORDER_STATUS.ALL
    setTabValue(tabFromUrl)
    setPage(1)
    // NOTE:(取消訂單後focus到該筆訂單) URL 參數帶入 focusedTransactionId，觸發 useEffect 自動換頁並 focus 到該筆訂單
    const focusedOrderFromUrl = (queryString.parse(search).focusedTransactionId as string) || null
    if (focusedOrderFromUrl) setFocusedTransactionId(focusedOrderFromUrl)
  }, [search])

  // NOTE:(取消訂單後focus到該筆訂單) state 有值後立即清除 URL param（避免重整後重新 focus）
  useEffect(() => {
    if (!focusedTransactionId) return
    if (!queryString.parse(search).focusedTransactionId) return // URL 已清除，避免重複觸發
    const { focusedTransactionId: _, ...rest } = queryString.parse(search)
    history.replace({ search: queryString.stringify(rest) })
  }, [focusedTransactionId, search, history])

  // NOTE:(取消訂單後focus到該筆訂單) focusedTransactionId 有值時，自動換頁直到找到目標訂單
  useEffect(function autoPageToFocusedOrder() {
    if (!focusedTransactionId || isLoading || !eCommerceOrders) return
    const isOnCurrentPage = eCommerceOrders.orders.some(
      order => order.payment_transaction_id === focusedTransactionId,
    )
    if (isOnCurrentPage) {
      hasFocusedRef.current = true // 已找到，停止自動換頁
      return
    }
    if (!hasFocusedRef.current && page < (eCommerceOrders.total_pages ?? 1)) {
      setPage(prev => prev + 1)
    }
  }, [focusedTransactionId, eCommerceOrders, isLoading, page])

  return (
    // TODO: 移除 SnackbarProvider，應該要在 App 層 init
    <SnackbarProvider>
      <SnackbarContext.Consumer>
        {(value) => {
          const { handleExited, snackbarProps, closeSnackbar } = value || {}
          return (
            <SnackbarVariants
              {...snackbarProps}
              autoHideDuration={3000}
              onClose={closeSnackbar}
              onExited={handleExited}
            />
          )
        }}
      </SnackbarContext.Consumer>
      <I18nextProvider i18n={wpMyAccountI18nInstance}>
        <Helmet>
          <title>{`${wpMyAccountI18nInstance.t(`wpOrderHtmlTitle.${projectCode}`)}`}</title>
        </Helmet>
        <WpMyAccountContext.Provider value={{ environment, orderStatus: tabValue, projectCode, campaignId }}>
          <Box width="100%" maxWidth="840px" mt={isWordPressMobile ? '-18px' : '0'}>
            <Tabs
              color={colors.primary600}
              variant={isWordPressMobile ? 'scrollable' : 'fullWidth'}
              tabData={TABS_WORDING_MAP[type]}
              value={tabValue}
              onChange={handleChangeTab}
            />
            {isLoading
              ? <OrderEmptyCard><CircularProgress fontSize={20} color={colors.grey300} /></OrderEmptyCard>
              : eCommerceOrders?.orders?.length === 0
                ? <OrderEmptyCard>{wpMyAccountI18nInstance.t('wpOrder.noOrder')}</OrderEmptyCard>
                : (
                  <>
                    <OrderList list={eCommerceOrders?.orders || []} type={type} status={tabValue} onRepayClick={onRepayClick} onCancelOrderClick={onCancelOrderClick} siteId={siteId} focusedTransactionId={focusedTransactionId} />
                    <Box py={4} display="flex" justifyContent="center">
                      <Pagination
                        sx={{ justifyContent: 'center' }}
                        variant="outlined"
                        count={eCommerceOrders?.total_pages || 1}
                        page={page}
                        onChange={handleChangePage}
                      />
                    </Box>
                  </>
                )}
          </Box>
        </WpMyAccountContext.Provider>
      </I18nextProvider>
    </SnackbarProvider>
  )
}
